<script lang="ts">
export default {
  name: "FrontendNavigationMobile",
};
</script>

<script lang="ts" setup>
import { FLEET_GLYPH } from "@/shared/glyphs/fleets";
import { SHIP_GLYPH } from "@/shared/glyphs/ships";
import AppNavigationMobile from "@/shared/components/AppNavigation/Mobile/index.vue";
import NavItem from "@/shared/components/AppNavigation/NavItem/index.vue";
import NotificationsNav from "@/frontend/components/Navigation/NotificationsNav/index.vue";
import { useSessionStore } from "@/frontend/stores/session";
import { storeToRefs } from "pinia";
import { useFleetRouteCheck } from "@/frontend/composables/useFleetRouteCheck";
import { useFleetNavAccess } from "@/frontend/composables/useFleetNavAccess";
import { useHangarStore } from "@/frontend/stores/hangar";
import {
  useFleet as useFleetQuery,
  usePublicFleet as usePublicFleetQuery,
  useMyFleets as useMyFleetsQuery,
} from "@/services/fyApi";

const sessionStore = useSessionStore();

const { isAuthenticated } = storeToRefs(sessionStore);

const route = useRoute();

const { isFleetRoute } = useFleetRouteCheck();

const hangarStore = useHangarStore();

const { preview: hangarPreview } = storeToRefs(hangarStore);

const routeActive = (routeName: string) => {
  return routeName === route.name;
};

const shipsNavActive = computed(() => {
  return ["fleet-ships", "fleet-fleetchart"].includes(String(route.name));
});

const fleetSlug = computed(() => {
  return route.params.slug as string;
});

const { data: fleetData } = useFleetQuery(fleetSlug, {
  query: {
    enabled: computed(() => isFleetRoute.value && isAuthenticated.value),
    retry: false,
  },
});

const { data: publicFleetData } = usePublicFleetQuery(fleetSlug, {
  query: {
    enabled: computed(
      () => isFleetRoute.value && (!isAuthenticated.value || !fleetData.value),
    ),
    retry: false,
  },
});

const currentFleet = computed(() => fleetData.value || publicFleetData.value);

const {
  showContractsNav,
  showEventsNav,
  eventsNavRoute,
  contractsNavActive,
  eventsNavActive,
} = useFleetNavAccess(currentFleet);

const { data: myFleets, isPending: myFleetsPending } = useMyFleetsQuery({
  query: {
    refetchOnWindowFocus: false,
    enabled: isAuthenticated,
  },
});

const primaryFleet = computed(() => myFleets.value?.[0]);
</script>

<template>
  <AppNavigationMobile v-if="route.name">
    <template v-if="isFleetRoute">
      <template v-if="currentFleet">
        <NavItem
          :to="{ name: 'fleet', params: { slug: currentFleet.slug } }"
          :image="currentFleet.logo?.smallUrl"
          :active="routeActive('fleet')"
          :label="currentFleet.name"
        />
        <NavItem
          v-if="currentFleet.publicFleet || currentFleet.myFleet"
          :to="{ name: 'fleet-ships', params: { slug: currentFleet.slug } }"
          :active="shipsNavActive"
          :icon="SHIP_GLYPH"
        />
        <NavItem
          v-if="currentFleet.myFleet"
          :to="{
            name: 'fleet-members',
            params: { slug: currentFleet.slug },
          }"
          :active="String(route.name).startsWith('fleet-members')"
          icon="fa-duotone fa-users"
        />
        <NavItem
          v-if="showContractsNav"
          data-tour="fleet-contracts"
          :to="{
            name: 'fleet-contracts',
            params: { slug: currentFleet.slug },
          }"
          :active="contractsNavActive"
          icon="fa-duotone fa-clipboard-list"
        />
        <NavItem
          v-if="showEventsNav"
          data-tour="fleet-events"
          :to="{
            name: eventsNavRoute,
            params: { slug: currentFleet.slug },
          }"
          :active="eventsNavActive"
          :icon="
            eventsNavRoute === 'fleet-missions'
              ? 'fa-duotone fa-flag-checkered'
              : 'fa-duotone fa-calendar-day'
          "
        />
      </template>
    </template>
    <template v-else>
      <NavItem
        :to="{ name: 'home' }"
        :active="routeActive('home')"
        icon="fa-duotone fa-home-alt"
      />
      <NavItem :to="{ name: 'ships' }" :icon="SHIP_GLYPH" />
      <NavItem
        v-if="isAuthenticated || !hangarPreview"
        :to="{ name: 'hangar' }"
        icon="fa-duotone fa-warehouse"
      />
      <NavItem
        v-else
        :to="{ name: 'hangar-preview' }"
        icon="fa-light fa-warehouse"
      />
      <NavItem
        v-if="primaryFleet"
        :to="{ name: 'fleet', params: { slug: primaryFleet.slug } }"
        :image="primaryFleet.logo?.smallUrl"
        :icon="FLEET_GLYPH"
      />
      <NotificationsNav
        v-else-if="isAuthenticated && !myFleetsPending"
        icon-only
      />
    </template>
  </AppNavigationMobile>
</template>
